import { Context, isiOS, isMobileDevice } from "@needle-tools/engine";
import { StateManager } from "./scripts/GameStateManager";
import { EulerStateManager } from "./scripts/EulerStateManager";
import { TabFocus } from "./scripts/TabFocus";
import { Timings } from "./scripts/Consts";
import "./styles/tailwind.css";
import "./scripts/UI";
import config from "../../newProject/game.config.json";

const ENABLE_LOGGING = true;

if (!ENABLE_LOGGING) {
  console.log = () => {};
  console.info = () => {};
  console.warn = () => {};
  console.debug = () => {};
}

interface Event {
  id: string;
  status: 'closed' | 'ready' | 'running' | 'pending';
  startTime: string;
  channel: string;
}

interface EventsResponse {
  [key: string]: Event;
}


const stateManager = StateManager.getInstance();
const eulerStateManager = EulerStateManager.getInstance();
const tabFocus = new TabFocus();
const eventTimeInMinutes = 3; // Event duration in minutes
let currentEventStartTime: string = "";
let currentEventId: string | null = null;

const HIDDEN_STATE = "hidden";

fetchCurrentEvent(config.channel, "1");

/*
 * Register the state flow for states that require changes based on Euler
 */
function RegisterEventFlow(joinRunningEvent: boolean) {
  if (joinRunningEvent === false) {
    console.log("Joining the next event");
    stateManager.changeState(stateManager.preEventGameState); //Set the entry point for both state machines
    eulerStateManager.changeState(eulerStateManager.eulerGetNextEventState); //Set the entry point for both state machines
  } else {
    console.log("Joining the current event");
    stateManager.changeState(stateManager.warmUpState); //Set the entry point for both state machines
    eulerStateManager.changeState(eulerStateManager.eulerGetCurrentEventState); //Set the entry point for both state machines
  }
}

/**
 *
 * TODO - fix all this up. Just testing for now
 */

async function fetchCurrentEvent(
  channel: string,
  margin: string,
): Promise<void> {
  const url = `https://${config.URL}/event/current/${config?.channel}/${margin}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Failed to fetch event data");
    }
    const events: EventsResponse = await response.json();
    
    // Get all events as an array, maintaining their original order
    const eventArray = Object.values(events);

    // Find the first event that is either pending or ready
    const currentEvent = eventArray.find(event => 
      event.status === 'pending' || event.status === 'ready'
    );

    if (!currentEvent) {
      console.error('No upcoming event found');
      return;
    }

    // Store both the event ID and start time
    currentEventId = currentEvent.id;
    currentEventStartTime = currentEvent.startTime;

    console.log("Event start time is ", currentEventStartTime);
    checkEventTime(currentEvent.startTime);

    console.log('Found event:', {
      id: currentEvent.id,
      status: currentEvent.status,
      startTime: currentEvent.startTime
    });
  } catch (error) {
    console.error("Error:", error);
  }
}


document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === HIDDEN_STATE) {
    // Do nothing when tab becomes hidden
  } else {
    // Check event status when tab becomes visible
    CheckIfEventIsOver();
  }
})


async function CheckIfEventIsOver() {
  try {
    const response = await fetch(`https://${config.URL}/event/current/${config?.channel}/1`);
    if (!response.ok) {
      console.error("Failed to fetch event data");
      return; // Don't reload if the API fails
    }
    
    const events: EventsResponse = await response.json();
    
    // Find our tracked event in the response
    const trackedEvent = Object.values(events).find(event => event.id === currentEventId);
    
    // Only reload if the event was previously known AND now it's missing or closed
    if (currentEventId && !trackedEvent) {
      console.log("Event no longer in list - triggering reload");
      Reload();
      return;
    }

    if (trackedEvent && trackedEvent.status === 'closed') {
      console.log("Event is marked as closed - triggering reload");
      Reload();
      return;
    }

    if (trackedEvent && trackedEvent.status === 'running') {
      // Only check timing if we have a valid start time
      if (!currentEventStartTime) {
        console.log("No start time available, skipping time check");
        return;
      }
      
      const eventDurationInMs = eventTimeInMinutes * 60 * 1000;
      const eventStartTime = new Date(currentEventStartTime);
      const eventEndTime = new Date(eventStartTime.getTime() + eventDurationInMs);
      const currentTime = new Date();

      console.log("Event timing check:", {
        currentTime: currentTime.toISOString(),
        eventStartTime: eventStartTime.toISOString(),
        eventEndTime: eventEndTime.toISOString(),
        isAfterEnd: currentTime.getTime() > eventEndTime.getTime() + 1000
      });

      if (currentTime.getTime() > eventEndTime.getTime() + 1000) {
        console.log("Event has ended based on timing - triggering reload");
        Reload();
        return;
      }
    }

  } catch (error) {
    console.error("Error checking event status:", error);
  }
}

function Reload() {
  window.location.reload();
}

function checkEventTime(startTime: string): void {
  const eventStartTime = new Date(startTime);
  const eventEndTime = new Date(
    eventStartTime.getTime() + Timings.RACE_START_GRACE_PERIOD * 1000,
  ); // Add 30 seconds
  const currentTime = new Date();

  if (currentTime >= eventStartTime && currentTime <= eventEndTime) {
    console.log(
      "Current time is within 30 seconds after the event start time.",
    );
    //Log how many seconds after the event start time the current time is
    const secondsAfterEventStart =
      (currentTime.getTime() - eventStartTime.getTime()) / 1000;
    console.log(
      `Current time is ${secondsAfterEventStart} seconds after the event start time.`);
    RegisterEventFlow(true);
  } else {
    console.log("Current time is not within the 30-second range.");
    RegisterEventFlow(false);
  }
}

/*
  This function sets up the device based on the device type. 
*/
function deviceSetup() {
  if (isMobileDevice() || isMacOS()) {
    Context.DefaultWebGLRendererParameters.powerPreference =
      config.powerPreference;

    if (isiOS()) {
      //Context.DefaultWebGLRendererParameters.antialias = ApplicationConfig.antialias; //Avoids a bug thank tanks the performance on iOS (bug reported to apple https://bugs.webkit.org/show_bug.cgi?id=276959)
    }
  }
}

//TODO - Move to helper-fuc.ts???
let __isMacOs: boolean | undefined;
export function isMacOS() {
  if (__isMacOs !== undefined) return __isMacOs;
  if (navigator.userAgentData) {
    // Use modern UA Client Hints API if available
    return (__isMacOs = navigator.userAgentData.platform === "macOS");
  } else {
    // Fallback to user agent string parsing
    const userAgent = navigator.userAgent.toLowerCase();
    return (__isMacOs =
      userAgent.includes("mac os x") || userAgent.includes("macintosh"));
  }
}
